import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Switch,
  Tabs,
  Alert,
  notification,
  Modal,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  InputNumber
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  ExperimentOutlined,
  SecurityScanOutlined,
  AppstoreOutlined,
  MailOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { adminApi } from '../services/platformApi';

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;

interface SystemSettings {
  // LLM Configuration
  llm_provider: 'bedrock' | 'anthropic' | 'groq' | 'openai';
  bedrock_model_id?: string;
  bedrock_region: string;
  custom_model_arn?: string;
  anthropic_api_key?: string;
  anthropic_model?: string;
  groq_api_key?: string;
  groq_model?: string;
  openai_api_key?: string;
  openai_model?: string;
  
  // Processing Configuration
  max_document_size_mb: number;
  max_concurrent_jobs: number;
  job_timeout_seconds: number;
  batch_size: number;
  enable_ocr: boolean;
  
  // Security Settings
  enable_audit_logging: boolean;
  session_timeout_minutes: number;
  max_login_attempts: number;
  require_mfa: boolean;
  allowed_domains: string[];
  
  // Feature Flags
  enable_legal_module: boolean;
  enable_defense_module: boolean;
  enable_data_connectors: boolean;
  enable_api_access: boolean;
  
  // Resource Limits
  max_users: number;
  max_storage_gb: number;
  max_api_calls_per_minute: number;
  
  // Notification Settings
  smtp_host?: string;
  smtp_port: number;
  smtp_user?: string;
  smtp_password?: string;
  notification_email?: string;
}

interface ModelInfo {
  model_id: string;
  provider: string;
  name: string;
  description: string;
  max_tokens: number;
  context_window: number;
  input_price_per_1k: number;
  output_price_per_1k: number;
  supports_streaming: boolean;
  supports_system_prompt: boolean;
}

const SettingsManager: React.FC = () => {
  const [form] = Form.useForm();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [bedrockModels, setBedrockModels] = useState<ModelInfo[]>([]);
  const [modelCategories, setModelCategories] = useState<any>({});
  const [customModelModal, setCustomModelModal] = useState(false);
  const [customModelArn, setCustomModelArn] = useState('');

  useEffect(() => {
    loadSettings();
    loadBedrockModels();
  }, []);

  useEffect(() => {
    if (settings) {
      form.setFieldsValue(settings);
    }
  }, [settings, form]);

  const loadSettings = async () => {
    try {
      const data = await adminApi.getSettings();
      setSettings(data);
      setLoading(false);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to load settings'
      });
      setLoading(false);
    }
  };

  const loadBedrockModels = async () => {
    try {
      const [models, categories] = await Promise.all([
        adminApi.getBedrockModels(),
        adminApi.getModelCategories()
      ]);

      setBedrockModels(models);
      setModelCategories(categories);
    } catch (error) {
      console.error('Failed to load Bedrock models:', error);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      await adminApi.updateSettings(values);
      setSettings(values);
      notification.success({
        message: 'Success',
        description: 'Settings saved successfully!'
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to save settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    const values = form.getFieldsValue();

    try {
      await adminApi.testLlmConnection({
        provider: values.llm_provider,
        model_id: values.bedrock_model_id || values.custom_model_arn,
        api_key: values.anthropic_api_key || values.groq_api_key || values.openai_api_key,
        region: values.bedrock_region
      });

      notification.success({
        message: 'Connection Test',
        description: 'LLM connection successful!'
      });
    } catch (error: any) {
      notification.error({
        message: 'Connection Test Failed',
        description: error.response?.data?.detail || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const addCustomModel = () => {
    if (customModelArn) {
      form.setFieldsValue({ custom_model_arn: customModelArn });
      setCustomModelModal(false);
      setCustomModelArn('');
    }
  };

  const addDomain = () => {
    const domains = form.getFieldValue('allowed_domains') || [];
    const newDomain = prompt('Enter allowed domain:');
    if (newDomain && !domains.includes(newDomain)) {
      form.setFieldsValue({ allowed_domains: [...domains, newDomain] });
    }
  };

  const removeDomain = (domain: string) => {
    const domains = form.getFieldValue('allowed_domains') || [];
    form.setFieldsValue({ 
      allowed_domains: domains.filter((d: string) => d !== domain) 
    });
  };

  const getModelPrice = (modelId: string) => {
    const model = bedrockModels.find(m => m.model_id === modelId);
    if (model) {
      return `$${model.input_price_per_1k}/1K in, $${model.output_price_per_1k}/1K out`;
    }
    return 'Custom pricing';
  };

  if (loading || !settings) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', marginTop: '100px' }} />;
  }

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          System Settings
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ExperimentOutlined />}
            onClick={handleTestConnection}
            loading={testing}
          >
            Test Connection
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={saving}
          >
            Save Changes
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={settings}
      >
        <Tabs defaultActiveKey="ai-models">
          <TabPane
            tab={
              <span>
                <RobotOutlined />
                AI Models
              </span>
            }
            key="ai-models"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label="LLM Provider"
                  name="llm_provider"
                  tooltip="Choose your AI model provider"
                >
                  <Select>
                    <Option value="bedrock">AWS Bedrock</Option>
                    <Option value="anthropic">Anthropic</Option>
                    <Option value="groq">Groq</Option>
                    <Option value="openai">OpenAI</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Form.Item noStyle shouldUpdate={(prev: any, curr: any) => prev.llm_provider !== curr.llm_provider}>
                {({ getFieldValue }: any) => {
                  const provider = getFieldValue('llm_provider');
                  
                  if (provider === 'bedrock') {
                    return (
                      <>
                        <Col span={24}>
                          <Alert
                            message="Model Categories"
                            description={
                              <Space wrap>
                                {Object.entries(modelCategories).map(([category, models]: [string, any]) => (
                                  <Tag
                                    key={category}
                                    color="blue"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      const firstModel = models[0];
                                      if (firstModel) {
                                        form.setFieldsValue({ bedrock_model_id: firstModel.model_id });
                                      }
                                    }}
                                  >
                                    {category.replace('_', ' ')}
                                  </Tag>
                                ))}
                              </Space>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                          />
                        </Col>
                        
                        <Col span={18}>
                          <Form.Item
                            label="Bedrock Model"
                            name="bedrock_model_id"
                            tooltip="Select a pre-configured Bedrock model"
                          >
                            <Select
                              showSearch
                              placeholder="Select a model"
                              optionFilterProp="children"
                            >
                              {bedrockModels.map((model) => (
                                <Option key={model.model_id} value={model.model_id}>
                                  <div>
                                    <div>{model.name}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                      {model.description} • {getModelPrice(model.model_id)}
                                    </div>
                                  </div>
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        
                        <Col span={6}>
                          <Form.Item label=" ">
                            <Button
                              block
                              icon={<PlusOutlined />}
                              onClick={() => setCustomModelModal(true)}
                            >
                              Custom Model ARN
                            </Button>
                          </Form.Item>
                        </Col>

                        <Form.Item noStyle shouldUpdate={(prev: any, curr: any) => prev.custom_model_arn !== curr.custom_model_arn}>
                          {({ getFieldValue }: any) => {
                            const customArn = getFieldValue('custom_model_arn');
                            return customArn ? (
                              <Col span={24}>
                                <Alert
                                  message={`Using custom model: ${customArn}`}
                                  type="info"
                                  closable
                                  onClose={() => form.setFieldsValue({ custom_model_arn: undefined })}
                                />
                              </Col>
                            ) : null;
                          }}
                        </Form.Item>

                        <Col span={24}>
                          <Form.Item
                            label="AWS Region"
                            name="bedrock_region"
                            tooltip="AWS region for Bedrock service"
                          >
                            <Input placeholder="us-east-1" />
                          </Form.Item>
                        </Col>
                      </>
                    );
                  }

                  if (provider === 'anthropic') {
                    return (
                      <>
                        <Col span={12}>
                          <Form.Item
                            label="Anthropic API Key"
                            name="anthropic_api_key"
                            tooltip="Your Anthropic API key"
                          >
                            <Input.Password placeholder="sk-ant-..." />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Anthropic Model"
                            name="anthropic_model"
                            tooltip="Anthropic model name"
                          >
                            <Input placeholder="claude-3-sonnet-20240229" />
                          </Form.Item>
                        </Col>
                      </>
                    );
                  }

                  if (provider === 'groq') {
                    return (
                      <>
                        <Col span={12}>
                          <Form.Item
                            label="Groq API Key"
                            name="groq_api_key"
                            tooltip="Your Groq API key"
                          >
                            <Input.Password placeholder="gsk_..." />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Groq Model"
                            name="groq_model"
                            tooltip="Groq model name"
                          >
                            <Input placeholder="llama3-70b-8192" />
                          </Form.Item>
                        </Col>
                      </>
                    );
                  }

                  if (provider === 'openai') {
                    return (
                      <>
                        <Col span={12}>
                          <Form.Item
                            label="OpenAI API Key"
                            name="openai_api_key"
                            tooltip="Your OpenAI API key"
                          >
                            <Input.Password placeholder="sk-..." />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="OpenAI Model"
                            name="openai_model"
                            tooltip="OpenAI model name"
                          >
                            <Input placeholder="gpt-4-turbo-preview" />
                          </Form.Item>
                        </Col>
                      </>
                    );
                  }

                  return null;
                }}
              </Form.Item>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                <ThunderboltOutlined />
                Processing
              </span>
            }
            key="processing"
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Max Document Size (MB)"
                  name="max_document_size_mb"
                  tooltip="Maximum size for uploaded documents"
                >
                  <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Max Concurrent Jobs"
                  name="max_concurrent_jobs"
                  tooltip="Maximum number of parallel processing jobs"
                >
                  <InputNumber min={1} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Job Timeout (seconds)"
                  name="job_timeout_seconds"
                  tooltip="Maximum time for a job to complete"
                >
                  <InputNumber min={60} max={86400} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Batch Size"
                  name="batch_size"
                  tooltip="Number of documents to process in each batch"
                >
                  <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Enable OCR Processing"
                  name="enable_ocr"
                  valuePropName="checked"
                  tooltip="Enable Optical Character Recognition for scanned documents"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                <SecurityScanOutlined />
                Security
              </span>
            }
            key="security"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label="Enable Audit Logging"
                  name="enable_audit_logging"
                  valuePropName="checked"
                  tooltip="Log all user actions for security auditing"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Session Timeout (minutes)"
                  name="session_timeout_minutes"
                  tooltip="Time before user sessions expire"
                >
                  <InputNumber min={5} max={1440} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Max Login Attempts"
                  name="max_login_attempts"
                  tooltip="Maximum failed login attempts before account lockout"
                >
                  <InputNumber min={3} max={10} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Require Multi-Factor Authentication"
                  name="require_mfa"
                  valuePropName="checked"
                  tooltip="Require MFA for all user accounts"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Allowed Domains"
                  tooltip="Email domains allowed for user registration"
                >
                  <Form.Item noStyle shouldUpdate={(prev: any, curr: any) => prev.allowed_domains !== curr.allowed_domains}>
                    {({ getFieldValue }: any) => {
                      const domains = getFieldValue('allowed_domains') || [];
                      return (
                        <div>
                          <Space wrap style={{ marginBottom: 8 }}>
                            {domains.map((domain: string) => (
                              <Tag
                                key={domain}
                                closable
                                onClose={() => removeDomain(domain)}
                              >
                                {domain}
                              </Tag>
                            ))}
                            <Tag
                              style={{ background: '#fff', borderStyle: 'dashed', cursor: 'pointer' }}
                              onClick={addDomain}
                            >
                              <PlusOutlined /> Add Domain
                            </Tag>
                          </Space>
                          <Form.Item name="allowed_domains" noStyle>
                            <Input type="hidden" />
                          </Form.Item>
                        </div>
                      );
                    }}
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                <AppstoreOutlined />
                Features
              </span>
            }
            key="features"
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={5}>Module Access</Title>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Legal Discovery Intelligence Module"
                  name="enable_legal_module"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Defense Intelligence Module"
                  name="enable_defense_module"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Data Connectors"
                  name="enable_data_connectors"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="API Access"
                  name="enable_api_access"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                Resources
              </span>
            }
            key="resources"
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item
                  label="Max Users"
                  name="max_users"
                  tooltip="Maximum number of user accounts"
                >
                  <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Max Storage (GB)"
                  name="max_storage_gb"
                  tooltip="Maximum storage capacity"
                >
                  <InputNumber min={1} max={100000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="API Rate Limit (per minute)"
                  name="max_api_calls_per_minute"
                  tooltip="Maximum API calls per minute per user"
                >
                  <InputNumber min={10} max={10000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane
            tab={
              <span>
                <MailOutlined />
                Notifications
              </span>
            }
            key="notifications"
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label="SMTP Host"
                  name="smtp_host"
                  tooltip="SMTP server hostname"
                >
                  <Input placeholder="smtp.gmail.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="SMTP Port"
                  name="smtp_port"
                  tooltip="SMTP server port"
                >
                  <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="SMTP Username"
                  name="smtp_user"
                  tooltip="SMTP authentication username"
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="SMTP Password"
                  name="smtp_password"
                  tooltip="SMTP authentication password"
                >
                  <Input.Password />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Notification Email"
                  name="notification_email"
                  tooltip="Email address for system notifications"
                >
                  <Input type="email" placeholder="admin@example.com" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Form>

      <Modal
        title="Add Custom Model ARN"
        open={customModelModal}
        onOk={addCustomModel}
        onCancel={() => setCustomModelModal(false)}
        width={600}
      >
        <Input
          placeholder="arn:aws:bedrock:us-east-1:123456789:custom-model/my-model"
          value={customModelArn}
          onChange={(e: any) => setCustomModelArn(e.target.value)}
          style={{ marginTop: 16 }}
        />
        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
          Enter a custom Bedrock model ARN or any model ID not in the predefined list
        </Text>
      </Modal>
    </Card>
  );
};

export default SettingsManager;